// Kleine Branchengags auf dem Werkshof. Jeder Gag läuft von selbst in Abständen
// und lässt sich per Klick sofort auslösen.
import * as THREE from "three"
import gsap from "gsap"
import { COLORS } from "./palette.js"
import { at, box, cylinder, cow, person, mat } from "./builders.js"
import { say, introduce } from "./bubble.js"

const WOOD = 0x8a5a36
const CARDBOARD = 0xc9a46b
const HIT_MATERIAL = new THREE.MeshBasicMaterial({ visible: false })

function hitZone(radius, height, y = 0) {
  const zone = new THREE.Mesh(new THREE.CylinderGeometry(radius, radius, height, 10), HIT_MATERIAL)
  zone.position.y = y + height / 2
  return zone
}

function keepClear(factory, circles) {
  factory.userData.keepClear = [...(factory.userData.keepClear || []), ...circles.map(([x, z, r]) => ({ x, z, r }))]
}

const pick = (list) => list[Math.floor(Math.random() * list.length)]

// Basis: Zeitsteuerung für automatisches Auslösen.
class Gag {
  constructor(factory, { every = 15, first = 5 } = {}) {
    this.factory = factory
    this.every = every
    this.timer = first
    this.running = false
    this.targets = []
    this.people = null
  }

  attach(people) {
    this.people = people
  }

  target(object, character = null) {
    object.userData.gag = this
    object.userData.character = character
    this.targets.push(object)
  }

  update(dt, time) {
    this.animate?.(dt, time)
    if (this.running) return
    this.timer -= dt
    if (this.timer <= 0) this.trigger()
  }

  trigger() {
    if (this.running || !this.play) return
    this.running = true
    this.timer = this.every
    Promise.resolve(this.play()).finally(() => (this.running = false))
  }

  click(object) {
    const character = object.userData.character
    if (character) introduce(object, character, pick(character.quotes), { y: object.userData.bubbleY ?? 1.6 })
    this.trigger()
  }

  // Figuren in der Nähe drehen sich zu einem Punkt (z. B. zur Kuh).
  lookAt(point, radius = 6) {
    for (const figure of this.people?.figures || []) {
      if (figure.userData.walk) continue
      if (figure.position.distanceTo(point) > radius) continue
      gsap.to(figure.rotation, { y: Math.atan2(point.x - figure.position.x, point.z - figure.position.z), duration: 0.5 })
    }
  }

  nearestFigure(point) {
    let best = null
    for (const figure of this.people?.figures || []) {
      if (!best || figure.position.distanceTo(point) < best.position.distanceTo(point)) best = figure
    }
    return best
  }
}

const wait = (seconds) => new Promise((resolve) => gsap.delayedCall(seconds, resolve))
const tween = (target, vars) => new Promise((resolve) => gsap.to(target, { ...vars, onComplete: resolve }))

// --- Maschinenbau: Gabelstapler mit wackelnder Palette ----------------------

function forkliftModel() {
  const g = new THREE.Group()
  g.add(at(box(1, 0.6, 1.3, COLORS.orange), 0, 0.2, -0.1))
  g.add(at(box(1, 0.55, 0.4, COLORS.steelDark), 0, 0.2, -0.75))
  for (const [x, z] of [[-0.5, 0.35], [0.5, 0.35], [-0.5, -0.6], [0.5, -0.6]]) {
    const w = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.22, 0.16, 8), mat(COLORS.navy))
    w.rotation.z = Math.PI / 2
    w.position.set(x, 0.22, z)
    g.add(w)
  }
  for (const [x, z] of [[-0.42, -0.55], [0.42, -0.55], [-0.42, 0.25], [0.42, 0.25]]) g.add(at(box(0.06, 1.15, 0.06, COLORS.navy), x, 0.8, z))
  g.add(at(box(1, 0.06, 0.95, COLORS.navy), 0, 1.95, -0.15))
  const driver = person({ shirt: COLORS.navy600, helmet: COLORS.lime })
  driver.scale.setScalar(0.85)
  driver.position.set(0, 0.55, -0.2)
  g.add(driver)
  for (const x of [-0.3, 0.3]) g.add(at(box(0.08, 2, 0.08, COLORS.steelDark), x, 0.1, 0.62))
  const load = new THREE.Group()
  load.position.set(0, 0.3, 1.15)
  load.add(at(box(1, 0.12, 1, WOOD), 0, 0, 0))
  const crates = [0, 1, 2].map((i) => {
    const crate = at(box(0.62, 0.6, 0.62, i === 2 ? COLORS.green : COLORS.wallWarm), (i - 1) * 0.04, 0.12 + i * 0.6, 0)
    crate.userData.home = crate.position.clone()
    load.add(crate)
    return crate
  })
  g.add(load)
  g.userData.load = load
  g.userData.crates = crates
  return g
}

class ForkliftGag extends Gag {
  constructor(factory) {
    super(factory, { every: 16, first: 4 })
    this.inside = new THREE.Vector3(1.25, 0, 1.4)
    this.outside = new THREE.Vector3(1.25, 0, 4.7)
    this.forklift = forkliftModel()
    this.forklift.position.copy(this.inside)
    this.forklift.add(hitZone(1.2, 2.2))
    this.forklift.userData.bubbleY = 2.6
    factory.add(this.forklift)
    this.target(this.forklift, { name: "Uwe", role: "Staplerfahrer", quotes: ["Achtung, Stapler!", "Ich fahr seit 1987 Stapler. Rückwärts besser als vorwärts.", "Die Palette wackelt nicht. Die tanzt.", "Ob ich mich morgen krankmelde? Nee, dann fährt Kevin."] })
    keepClear(factory, [[1.25, 4, 1.6], [1.25, 5.4, 1.8], [2.3, 6, 1.2]])
    this.wobble = 0
  }

  animate(dt, time) {
    const crates = this.forklift.userData.crates
    crates.forEach((crate, i) => {
      if (crate.userData.falling) return
      crate.rotation.z = Math.sin(time * 6 + i) * this.wobble * (i + 1) * 0.06
    })
  }

  async play() {
    const crate = this.forklift.userData.crates[2]
    this.wobble = 1
    await tween(this.forklift.position, { z: this.outside.z, duration: 2.2, ease: "power1.inOut" })
    crate.userData.falling = true
    await tween(crate.position, { x: 0.9, y: -0.05, z: 0.7, duration: 0.55, ease: "bounce.out" })
    gsap.to(crate.rotation, { z: -1.2, x: 0.4, duration: 0.5 })
    say(this.forklift, "Ups!", { y: 2.6, duration: 1400 })
    this.wobble = 0.3
    await wait(1.4)
    say(this.forklift, "Piep! Piep! Piep!", { y: 2.6, duration: 2400, tone: "bump" })
    await tween(this.forklift.position, { z: this.inside.z, duration: 2.6, ease: "power1.inOut" })
    this.wobble = 0
    // Kiste kommt wieder auf den Stapel, solange niemand hinsieht
    crate.rotation.set(0, 0, 0)
    crate.position.copy(crate.userData.home)
    crate.userData.falling = false
  }
}

// --- Kunststoff: Quietscheente springt von Silo zu Silo -----------------------

function duckModel() {
  const g = new THREE.Group()
  const yellow = mat(0xf7d33c)
  const body = new THREE.Mesh(new THREE.IcosahedronGeometry(0.35, 1), yellow)
  body.scale.set(1.25, 0.85, 1)
  body.position.y = 0.3
  const head = new THREE.Mesh(new THREE.IcosahedronGeometry(0.22, 1), yellow)
  head.position.set(0.28, 0.68, 0)
  const beak = new THREE.Mesh(new THREE.ConeGeometry(0.09, 0.22, 5), mat(COLORS.orange))
  beak.rotation.z = -Math.PI / 2
  beak.position.set(0.52, 0.66, 0)
  for (const m of [body, head, beak]) m.castShadow = true
  g.add(body, head, beak)
  g.scale.setScalar(1.7)
  return g
}

class DuckGag extends Gag {
  constructor(factory) {
    super(factory, { every: 7, first: 3 })
    this.silos = [-8, -5.9, -3.8].map((x) => new THREE.Vector3(x, 6.75, -1.5))
    this.index = 1
    this.direction = 1
    this.duck = duckModel()
    this.duck.position.copy(this.silos[this.index])
    this.duck.add(hitZone(0.6, 1.2))
    this.duck.userData.bubbleY = 1.3
    factory.add(this.duck)
    this.target(this.duck, { name: "Quietschi", role: "Maskottchen", quotes: ["Quietsch!", "Ich bin aus 100 % Rezyklat. Behaupte ich.", "Quietsch – Scope 3, Kategorie 1!", "Inoffiziell bin ich hier die Chefin."] })
  }

  animate(dt, time) {
    if (!this.running) this.duck.rotation.y = Math.sin(time * 1.5) * 0.4
  }

  async play() {
    if (this.index + this.direction > 2 || this.index + this.direction < 0) this.direction *= -1
    const from = this.silos[this.index].clone()
    this.index += this.direction
    const to = this.silos[this.index]
    this.duck.rotation.y = this.direction > 0 ? 0 : Math.PI
    const state = { t: 0 }
    await tween(state, {
      t: 1, duration: 0.9, ease: "none",
      onUpdate: () => {
        this.duck.position.lerpVectors(from, to, state.t)
        this.duck.position.y += Math.sin(Math.PI * state.t) * 3
        this.duck.rotation.z = (0.5 - state.t) * 1.2 * this.direction
      }
    })
    this.duck.rotation.z = 0
    say(this.duck, "Quietsch!", { y: 1.3, duration: 1200, tone: "bump" })
    gsap.fromTo(this.duck.scale, { y: 1.2 }, { y: 1.7, duration: 0.35, ease: "elastic.out(1, 0.4)" })
  }
}

// --- Lebensmittel: Die Kuh drängelt sich ans Tor ------------------------------

class CowGag extends Gag {
  constructor(factory) {
    super(factory, { every: 18, first: 5 })
    this.path = new THREE.CatmullRomCurve3([
      new THREE.Vector3(-6.2, 0, 10.4), new THREE.Vector3(-6.2, 0, 7.2), new THREE.Vector3(-5, 0, 5.2), new THREE.Vector3(-3, 0, 4.5)
    ])
    this.cow = cow()
    this.cow.scale.setScalar(1.5)
    this.cow.add(hitZone(0.9, 1.4))
    this.cow.userData.bubbleY = 1.6
    this.place(0)
    factory.add(this.cow)
    this.target(this.cow, { name: "Berta", role: "Milchlieferantin", quotes: ["Muuuh!", "Ich bin Kategorie 1. Und stolz drauf.", "Muuuh – wo geht’s zur Kantine?", "Methan? Kein Kommentar."] })
    keepClear(factory, [[-6.2, 7.5, 1.6], [-5.2, 5.4, 1.6], [-3.2, 4.6, 1.4]])
  }

  place(t, backwards = false) {
    const point = this.path.getPointAt(t)
    const tangent = this.path.getTangentAt(t).multiplyScalar(backwards ? -1 : 1)
    this.cow.position.copy(point)
    this.cow.rotation.y = Math.atan2(-tangent.z, tangent.x)
  }

  async walk(from, to, backwards) {
    const state = { t: from }
    await tween(state, {
      t: to, duration: 4, ease: "power1.inOut",
      onUpdate: () => {
        this.place(state.t, backwards)
        this.cow.position.y = Math.abs(Math.sin(state.t * 40)) * 0.06
      }
    })
  }

  async play() {
    await this.walk(0, 1, false)
    say(this.cow, "Muuuh!", { y: 1.6, duration: 1800, tone: "bump" })
    this.lookAt(this.cow.position, 7)
    await wait(0.9)
    const figure = this.nearestFigure(this.cow.position)
    if (figure) say(figure, pick(["Berta ist wieder da!", "Die Lieferung ist zu früh!", "Hat jemand die Weide offen gelassen?"]), { duration: 2200 })
    await wait(2.2)
    await this.walk(1, 0, true)
  }
}

// --- Bau: Kran mit Azubi am Haken, Bagger schaufelt ---------------------------

class CraneGag extends Gag {
  constructor(factory) {
    super(factory, { every: 9, first: 4 })
    this.crane = factory.userData.crane
    this.excavator = factory.userData.excavator
    this.crane.userData.load.visible = false
    const hook = this.crane.userData.hook
    this.pivot = new THREE.Group()
    this.pivot.position.set(hook.x, hook.y, hook.z)
    this.crane.add(this.pivot)
    this.hanging = person({ shirt: COLORS.orange, helmet: COLORS.lime })
    this.hanging.scale.setScalar(1.45)
    this.hanging.userData.arms.forEach((shoulder, i) => (shoulder.rotation.z = i === 0 ? 3 : -3))
    this.hanging.position.y = -1.75
    this.hanging.add(hitZone(0.5, 1.3))
    this.hanging.userData.bubbleY = 1.6
    this.pivot.add(this.hanging)
    this.target(this.hanging, { name: "Jan", role: "Azubi", quotes: ["Huiii!", "Holt mich hier runter!", "Ich wollte nur die Aussicht genießen!", "Von hier oben sieht man die ganze Lieferkette!", "Ob das als Überstunde zählt?"] })
    this.lines = ["Huiii!", "Ich wollte nur die Aussicht genießen!", "Holt mich hier runter!", "Weiter links! Nein, rechts!"]
  }

  animate(dt, time) {
    this.crane.rotation.y = Math.sin(time * 0.22) * 0.9
    this.pivot.rotation.z = Math.sin(time * 1.7) * 0.22
    this.pivot.rotation.x = Math.sin(time * 1.1) * 0.12
    const arm = this.excavator.userData.arm
    arm.rotation.z = -0.5 + Math.sin(time * 1.3) * 0.35
    this.excavator.userData.bucket.position.y = 0.2 + Math.max(0, Math.sin(time * 1.3)) * 0.5
  }

  play() {
    say(this.hanging, this.lines[Math.floor(Math.random() * this.lines.length)], { y: 1.6, duration: 2200, tone: "bump" })
    return wait(2.2)
  }
}

// --- Handel: Der Paketturm wackelt und fällt um --------------------------------

class ParcelGag extends Gag {
  constructor(factory) {
    super(factory, { every: 22, first: 8 })
    this.tower = new THREE.Group()
    this.tower.position.set(7.6, 0, 5.6)
    this.boxes = Array.from({ length: 7 }, (_, i) => {
      const size = 0.75 - (i % 3) * 0.08
      const parcel = box(size, 0.5, size, i % 4 === 3 ? COLORS.wallWarm : CARDBOARD)
      parcel.position.set(Math.sin(i * 1.7) * 0.08, 0.25 + i * 0.5, Math.cos(i * 2.3) * 0.08)
      parcel.rotation.y = i * 0.3
      parcel.userData.home = { position: parcel.position.clone(), rotation: parcel.rotation.clone() }
      this.tower.add(parcel)
      return parcel
    })
    this.tower.add(hitZone(0.9, 3.6))
    this.tower.userData.bubbleY = 4
    factory.add(this.tower)
    this.target(this.tower, null)
    keepClear(factory, [[7.6, 5.6, 2.4]])
    this.fallen = false
  }

  animate(dt, time) {
    if (this.fallen) return
    this.boxes.forEach((parcel, i) => {
      parcel.rotation.z = Math.sin(time * 2.2) * 0.012 * i
      parcel.position.x = parcel.userData.home.position.x + Math.sin(time * 2.2) * 0.02 * i
    })
  }

  async play() {
    this.fallen = true
    this.boxes.forEach((parcel, i) => {
      const angle = -0.6 + Math.random() * 1.6
      const distance = 0.6 + i * 0.35
      gsap.to(parcel.position, { x: Math.cos(angle) * distance, y: 0.25, z: Math.sin(angle) * distance * 0.6, duration: 0.5 + i * 0.07, delay: i * 0.04, ease: "bounce.out" })
      gsap.to(parcel.rotation, { x: Math.random() * 1.5, z: (Math.random() - 0.5) * 3, duration: 0.6, delay: i * 0.04 })
    })
    await wait(0.7)
    say(this.tower, "Rumms!", { y: 1.5, duration: 1300, tone: "bump" })
    await wait(0.6)
    this.lookAt(this.tower.position, 8)
    const figure = this.nearestFigure(this.tower.position)
    if (figure) say(figure, pick(["Yusuuuf!", "Wer war das?!", "Das war so nicht geplant …"]), { duration: 2200 })
    await wait(4)
    // Wieder aufstapeln
    await Promise.all(this.boxes.map((parcel, i) => {
      const { position, rotation } = parcel.userData.home
      gsap.to(parcel.rotation, { x: rotation.x, y: rotation.y, z: rotation.z, duration: 0.4, delay: i * 0.12 })
      return tween(parcel.position, { x: position.x, y: position.y, z: position.z, duration: 0.4, delay: i * 0.12, ease: "back.out(2)" })
    }))
    this.fallen = false
  }

  click() {
    if (!this.running) this.trigger()
    else say(this.tower, "Schon gut, schon gut …", { y: 1.5, duration: 1200, force: true })
  }
}

// --- Dienstleistung: Yoga auf dem Dach, Tischtennis im Hof ---------------------

class OfficeGag extends Gag {
  constructor(factory) {
    super(factory, { every: 12, first: 6 })
    // Yoga auf dem Dach
    this.yogi = person({ shirt: COLORS.lime, skin: 0xe0ac7e })
    this.yogi.scale.setScalar(1.45)
    this.yogi.position.set(1.5, 7.8, -1.2)
    this.yogi.rotation.y = 0.5
    this.yogi.add(hitZone(0.5, 1.4))
    factory.add(at(box(1.4, 0.05, 0.7, COLORS.green), 1.5, 7.76, -1.2, 0.5))
    factory.add(this.yogi)
    this.target(this.yogi, { name: "Charlotte", role: "Geschäftsführerin", quotes: ["Namasté – und Scope 3 im Blick.", "Einatmen. Ausatmen. Emissionen senken.", "Der herabschauende Hund heißt hier: herabschauender Vorstand.", "Meine besten Entscheidungen fälle ich im Sonnengruß."] })
    this.poses = [
      { arms: [3, -3], bend: 0 },
      { arms: [3, -3], bend: 0.35 },
      { arms: [1.5, -1.5], bend: 0 },
      { arms: [3, -3], bend: -0.35 }
    ]
    this.pose = 0
    this.poseTimer = 0

    // Tischtennis
    this.table = new THREE.Group()
    this.table.position.set(-5.5, 0, 2.8)
    this.table.add(at(box(2.4, 0.08, 1.3, COLORS.green), 0, 0.72, 0))
    this.table.add(at(box(0.04, 0.16, 1.4, COLORS.white), 0, 0.8, 0))
    for (const [x, z] of [[-1, -0.5], [1, -0.5], [-1, 0.5], [1, 0.5]]) this.table.add(at(box(0.08, 0.72, 0.08, COLORS.navy), x, 0, z))
    this.players = [-1.75, 1.75].map((x, i) => {
      const player = person({ shirt: i === 0 ? COLORS.red : COLORS.navy600, skin: i === 0 ? 0xf2c9a0 : 0x8d5a3b })
      player.scale.setScalar(1.3)
      player.position.set(x, 0, 0)
      player.rotation.y = i === 0 ? Math.PI / 2 : -Math.PI / 2
      player.add(hitZone(0.5, 1.4))
      this.table.add(player)
      return player
    })
    this.target(this.players[0], { name: "Jakob", role: "Werkstudent", quotes: ["11:9 für mich!", "Ungeschlagener Tischtennis-Champion seit drei Semestern.", "Noch ein Satz, dann arbeite ich weiter. Ehrlich."] })
    this.target(this.players[1], { name: "Moritz", role: "Junior Consultant", quotes: ["Das war ein Netzroller!", "Ich hab dazu eine Folie vorbereitet.", "Revanche nach dem Call!"] })
    this.ball = new THREE.Mesh(new THREE.SphereGeometry(0.09, 6, 5), mat(COLORS.white, { emissive: COLORS.white, emissiveIntensity: 0.3 }))
    this.table.add(this.ball)
    this.rally = 0
    factory.add(this.table)
    keepClear(factory, [[-5.5, 2.8, 2.9]])
  }

  animate(dt, time) {
    // Yoga-Posen wechseln alle 2,5 s
    this.poseTimer -= dt
    if (this.poseTimer <= 0) {
      this.poseTimer = 2.5
      this.pose = (this.pose + 1) % this.poses.length
      const pose = this.poses[this.pose]
      this.yogi.userData.arms.forEach((shoulder, i) => gsap.to(shoulder.rotation, { z: pose.arms[i], duration: 0.8, ease: "power2.inOut" }))
      gsap.to(this.yogi.rotation, { z: pose.bend, duration: 0.8, ease: "power2.inOut" })
    }

    // Ballwechsel, ab und zu geht ein Ball ins Aus
    if (this.miss) return
    this.rally += dt / 0.85
    const hit = Math.floor(this.rally)
    const p = this.rally - hit
    const leftToRight = hit % 2 === 0
    const fromX = leftToRight ? -1.1 : 1.1
    this.ball.position.set(fromX + (leftToRight ? 2.2 : -2.2) * p, 0.8 + Math.abs(Math.sin(Math.PI * p * 2)) * 0.45, Math.sin(hit * 1.7) * 0.25 * p)
    if (hit !== this.lastHit) {
      this.lastHit = hit
      const player = this.players[leftToRight ? 0 : 1]
      const shoulder = player.userData.arms[1]
      gsap.fromTo(shoulder.rotation, { x: 0 }, { x: -1.4, duration: 0.15, yoyo: true, repeat: 1 })
    }
  }

  async play() {
    // Der Ball fliegt ins Aus
    this.miss = true
    await tween(this.ball.position, { x: 3.6, y: 0.1, z: 1.4, duration: 0.7, ease: "power1.in" })
    say(this.players[0], pick(["Aus! 11:9 für mich!", "Jaaa!", "Punkt!"]), { y: 1.6, duration: 1800, tone: "bump" })
    await wait(0.4)
    say(this.players[1], pick(["Der war drin!", "Netzroller!", "Revanche!"]), { y: 1.6, duration: 1800 })
    await wait(1.8)
    this.rally = 0
    this.lastHit = undefined
    this.miss = false
  }
}

const GAGS = {
  maschinenbau: ForkliftGag,
  kunststoff: DuckGag,
  lebensmittel: CowGag,
  bau: CraneGag,
  handel: ParcelGag,
  dienstleistung: OfficeGag
}

export function buildGag(industry, factory) {
  const GagClass = GAGS[industry]
  return GagClass ? new GagClass(factory) : null
}

export function gagTargetFor(object) {
  let current = object
  while (current) {
    if (current.userData?.gag) return current
    current = current.parent
  }
  return null
}
